import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, ArrowLeft, CheckCircle2, Ticket } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import Header from "../components/Header";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";

export default function FormFill() {
  const { formId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(undefined); // undefined=checking, null=not registered, obj=registered

  // If a logged-in user already registered for this form, block the form and show their ticket
  useEffect(() => {
    if (!user || user === false) {
      setExisting(null);
      return;
    }
    api
      .get("/my/tickets")
      .then((r) => setExisting((r.data || []).find((s) => s.form_id === formId) || null))
      .catch(() => setExisting(null));
  }, [user, formId]);

  useEffect(() => {
    const url = `/forms/${formId}` + (token ? `?token=${encodeURIComponent(token)}` : "");
    api
      .get(url)
      .then((r) => setForm(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [formId, token]);

  // Prefill from logged-in user's profile (one-click registration)
  useEffect(() => {
    if (!form || !user || user === false) return;
    setValues((v) => {
      const next = { ...v };
      for (const f of form.fields || []) {
        if (next[f.key]) continue;
        const keyL = (f.key || "").toLowerCase();
        const label = `${f.label || ""} ${f.label_ar || ""}`;
        if (f.type === "email") next[f.key] = user.email || "";
        else if (f.type === "tel") next[f.key] = user.phone || "";
        else if (keyL.includes("national") || label.includes("هوية")) next[f.key] = user.national_id || "";
        else if ((keyL.includes("name") || label.includes("اسم")) && f.type === "text") next[f.key] = user.name || "";
      }
      return next;
    });
  }, [form, user]);

  const setVal = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const validateFields = () => {
    for (const f of form.fields || []) {
      const raw = values[f.key];
      if (raw == null || typeof raw !== "string" || !raw.trim()) continue;
      const v = raw.trim();
      const keyL = (f.key || "").toLowerCase();
      const label = `${f.label || ""} ${f.label_ar || ""}`;
      if (f.type === "tel") {
        const digits = v.replace(/\D/g, "");
        if (!/^05\d{8}$/.test(digits)) return t("errPhone");
      } else if (keyL.includes("national") || label.includes("هوية")) {
        if (!/^\d{10}$/.test(v)) return t("errNationalId");
      } else if ((keyL.includes("name") || label.includes("اسم")) && f.type === "text") {
        if (v.split(/\s+/).filter(Boolean).length < 3) return t("errFullName");
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const vErr = validateFields();
    if (vErr) {
      toast.error(vErr);
      return;
    }
    setSubmitting(true);
    try {
      const url =
        `/forms/${formId}/submit` + (token ? `?token=${encodeURIComponent(token)}` : "");
      const { data } = await api.post(url, { data: values });
      navigate(`/ticket/${data.ticket_code}`, { state: { submission: data, form } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (
        err.response?.status === 409 ||
        (typeof detail === "string" && detail.startsWith("duplicate:"))
      ) {
        toast.error(t("duplicateError"));
      } else {
        toast.error(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="max-w-2xl mx-auto p-8">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
            <p data-testid="form-error" className="text-red-700">{error}</p>
            <Link to="/">
              <Button variant="outline" className="mt-4 rounded-full">
                <ArrowLeft className="w-4 h-4 me-2 rtl:rotate-180" />
                {t("backHome")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="max-w-2xl mx-auto p-8 space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const title = (lang === "ar" ? form.title_ar : form.title) || form.title;
  const desc = (lang === "ar" ? form.description_ar : form.description) || form.description;

  // still verifying an existing registration for a logged-in user — avoid flashing the form
  if (user && user !== false && existing === undefined) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="max-w-2xl mx-auto p-8 space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (existing) {
    const pending = existing.approved === false;
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="max-w-lg mx-auto px-4 sm:px-6 py-10">
          <div data-testid="already-registered-card" className="bg-white rounded-3xl border border-emerald-200 p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 border border-emerald-200 grid place-items-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold mb-4">
              {t("alreadyRegisteredBadge")}
            </div>
            <h1 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 mb-3">{title}</h1>
            <p className="text-sm text-slate-600 leading-relaxed">{t("alreadyRegisteredHint")}</p>
            {!pending && existing.ticket_code && (
              <p className="mt-3 font-mono text-lg font-bold tracking-widest text-slate-800" dir="ltr">
                {existing.ticket_code}
              </p>
            )}
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              {!pending && existing.ticket_code && (
                <Link to={`/ticket/${existing.ticket_code}`}>
                  <Button data-testid="already-registered-ticket-btn" className="rounded-full bg-blue-600 hover:bg-blue-700">
                    <Ticket className="w-4 h-4 me-2" />
                    {t("viewMyTicket")}
                  </Button>
                </Link>
              )}
              <Link to="/">
                <Button data-testid="already-registered-home-btn" variant="outline" className="rounded-full">
                  {t("backHome")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-blue-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
          {t("backHome")}
        </Link>

        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
          {form.cover_image && (
            <div className="aspect-[16/7] overflow-hidden">
              <img
                src={form.cover_image}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="p-6 sm:p-10">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">
              {title}
            </h1>
            {desc && <p className="mt-3 text-slate-600 leading-relaxed">{desc}</p>}

            <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-500">
              {form.event_date && (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4" /> {form.event_date}
                </span>
              )}
              {form.location && (
                form.location_url ? (
                  <a
                    href={form.location_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="location-map-link"
                    onClick={(e) => {
                      if (isNativeApp()) {
                        e.preventDefault();
                        openExternal(form.location_url);
                      }
                    }}
                    className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-800 underline decoration-blue-300 underline-offset-4"
                  >
                    <MapPin className="w-4 h-4" /> {form.location}
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" /> {form.location}
                  </span>
                )
              )}
              {form.max_submissions && (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  {form.submissions_count || 0} {t("of")} {form.max_submissions}
                </span>
              )}
            </div>

            {!form.open ? (
              <div
                data-testid="form-closed-banner"
                className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800 text-sm"
              >
                {t("formClosed")}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                {form.fields.map((f) => (
                  <FieldRenderer
                    key={f.key}
                    field={f}
                    value={values[f.key]}
                    onChange={(v) => setVal(f.key, v)}
                    lang={lang}
                  />
                ))}

                <Button
                  data-testid="submit-registration-btn"
                  type="submit"
                  disabled={submitting}
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                >
                  {submitting ? t("submitting") : t("submitRegistration")}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, lang }) {
  const label = (lang === "ar" ? field.label_ar : field.label) || field.label;
  const req = field.required;
  const testId = `field-${field.key}`;

  const commonLabel = (
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
      {label}
      {req && <span className="text-red-500 ms-1">*</span>}
    </Label>
  );

  if (field.type === "textarea") {
    return (
      <div>
        {commonLabel}
        <Textarea
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          required={req}
          rows={4}
        />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        {commonLabel}
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={testId}>
            <SelectValue placeholder={field.placeholder || "—"} />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "radio") {
    return (
      <div>
        {commonLabel}
        <RadioGroup value={value || ""} onValueChange={onChange} data-testid={testId}>
          {(field.options || []).map((opt) => (
            <div key={opt} className="flex items-center gap-2">
              <RadioGroupItem value={opt} id={`${field.key}-${opt}`} />
              <Label htmlFor={`${field.key}-${opt}`} className="cursor-pointer text-sm">
                {opt}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>
    );
  }
  if (field.type === "checkbox") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      if (arr.includes(opt)) onChange(arr.filter((x) => x !== opt));
      else onChange([...arr, opt]);
    };
    return (
      <div>
        {commonLabel}
        <div data-testid={testId} className="space-y-2">
          {(field.options || []).map((opt) => (
            <div key={opt} className="flex items-center gap-2">
              <Checkbox
                id={`${field.key}-${opt}`}
                checked={arr.includes(opt)}
                onCheckedChange={() => toggle(opt)}
              />
              <Label htmlFor={`${field.key}-${opt}`} className="cursor-pointer text-sm">
                {opt}
              </Label>
            </div>
          ))}
        </div>
      </div>
    );
  }
  // text-like inputs
  const inputType =
    field.type === "email"
      ? "email"
      : field.type === "tel"
      ? "tel"
      : field.type === "number"
      ? "number"
      : field.type === "date"
      ? "date"
      : "text";
  const isNationalId = (field.key || "").toLowerCase().includes("national") || `${field.label || ""}${field.label_ar || ""}`.includes("هوية");
  const numeric = field.type === "tel" || isNationalId;
  return (
    <div>
      {commonLabel}
      <Input
        data-testid={testId}
        type={inputType}
        value={value || ""}
        onChange={(e) => {
          const v = numeric ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value;
          onChange(v);
        }}
        placeholder={field.placeholder || (field.type === "tel" ? "05XXXXXXXX" : isNationalId ? "1XXXXXXXXX" : "")}
        required={req}
        inputMode={numeric ? "numeric" : undefined}
        maxLength={numeric ? 10 : undefined}
        dir={numeric ? "ltr" : undefined}
        className={`h-12 text-base ${numeric ? "text-end tracking-wider" : ""}`}
      />
    </div>
  );
}
